import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SITE } from "@/config/site";
import { track } from "@/lib/analytics";

const inputCls =
  "w-full rounded-full border border-forest/20 bg-white/70 px-5 py-3.5 text-sm text-charcoal placeholder:text-taupe/70 outline-none transition-colors duration-300 focus:border-gold focus:bg-white";

export const GoogleButton = ({ testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={() => {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/my-wellness-journey";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }}
    className="inline-flex w-full items-center justify-center gap-3 rounded-full border border-forest/20 bg-white/80 px-8 py-4 text-sm font-semibold text-forest transition-all duration-300 hover:border-gold"
  >
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
      <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.3-2.1 3.7-5.1 3.7-8.6z" />
      <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-5.9-2.1-6.8-5.1L1.3 17.2C3.3 21.2 7.3 24 12 24z" />
      <path fill="#FBBC05" d="M5.2 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3L1.3 6.8C.5 8.4 0 10.1 0 12s.5 3.6 1.3 5.2l3.9-2.9z" />
      <path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.3 0 3.3 2.8 1.3 6.8l3.9 2.9c.9-2.9 3.6-5 6.8-5z" />
    </svg>
    Continue with Google
  </button>
);

export default function Register() {
  const navigate = useNavigate();
  const { setMember } = useAuth();
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    consent_terms: false,
    consent_newsletter: false,
    consent_reminders: false,
  });

  useEffect(() => {
    document.title = "Create Your Free Account | Rooted Healing";
  }, []);

  const set = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.password) {
      toast.error("Please complete your name, email, and password.");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Please choose a password of at least 8 characters.");
      return;
    }
    if (!form.consent_terms) {
      toast.error("Please accept the privacy policy and account terms to continue.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", {
        name: form.name,
        email: form.email,
        password: form.password,
        phone: form.phone || null,
        consent_terms: form.consent_terms,
        consent_newsletter: form.consent_newsletter,
        consent_reminders: form.consent_reminders,
      });
      setMember(data);
      track("registration_completed");
      toast.success(`Welcome, ${data.name.split(" ")[0]}. Your account is ready.`);
      navigate("/assessment");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <main data-testid="register-page" className="pt-[76px]">
      <div className="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-2">
        <div className="relative hidden min-h-[640px] lg:block">
          <img
            src={SITE.images.finalBg}
            alt="Staci Freed seated peacefully among forest ferns in soft light"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div aria-hidden="true" className="absolute inset-0 bg-forest-deep/55" />
          <div className="relative flex h-full flex-col justify-end p-12">
            <p className="font-serif text-2xl italic leading-relaxed text-ivory">
              "Discover what is supporting you, what is asking for attention, and where your
              journey toward greater balance can begin."
            </p>
            <p className="mt-4 text-xs uppercase tracking-[0.25em] text-ivory/60">
              The Rooted Wellness Discovery
            </p>
          </div>
        </div>

        <div className="px-5 py-16 sm:px-10 sm:py-20 lg:px-16">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold">
              Member Registration
            </p>
            <h1 className="mt-4 font-serif text-3xl leading-tight text-forest sm:text-4xl">
              Create Your Free Wellness Account
            </h1>
            <p className="mt-4 leading-relaxed text-charcoal/70">
              Your account holds your wellness discovery, personal timeline, and private
              reports. Only you can see your answers.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-8">
              <GoogleButton testid="register-google-button" />
              <div className="my-6 flex items-center gap-4 text-xs uppercase tracking-[0.2em] text-taupe">
                <span className="h-px flex-1 bg-forest/15" />
                or with email
                <span className="h-px flex-1 bg-forest/15" />
              </div>
            </div>
            <form data-testid="register-form" onSubmit={onSubmit}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="register-full-name" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-forest">
                    Full name
                  </label>
                  <input
                    id="register-full-name"
                    data-testid="register-full-name"
                    type="text"
                    autoComplete="name"
                    placeholder="Your full name"
                    value={form.name}
                    onChange={set("name")}
                    onFocus={() => track("registration_started")}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="register-email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-forest">
                    Email address
                  </label>
                  <input
                    id="register-email"
                    data-testid="register-email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={set("email")}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="register-password" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-forest">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="register-password"
                      data-testid="register-password"
                      type={showPw ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder="At least 8 characters"
                      value={form.password}
                      onChange={set("password")}
                      className={`${inputCls} pr-12`}
                    />
                    <button
                      type="button"
                      data-testid="register-password-toggle"
                      onClick={() => setShowPw((v) => !v)}
                      aria-label={showPw ? "Hide password" : "Show password"}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-taupe transition-colors hover:text-forest"
                    >
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label htmlFor="register-phone" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-forest">
                    Phone <span className="normal-case text-taupe">(optional)</span>
                  </label>
                  <input
                    id="register-phone"
                    data-testid="register-phone"
                    type="tel"
                    autoComplete="tel"
                    placeholder="Optional"
                    value={form.phone}
                    onChange={set("phone")}
                    className={inputCls}
                  />
                </div>
              </div>

              <div className="mt-7 flex flex-col gap-4">
                <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-charcoal/70">
                  <input
                    type="checkbox"
                    data-testid="register-consent-terms"
                    checked={form.consent_terms}
                    onChange={set("consent_terms")}
                    className="brand-check mt-0.5 h-4 w-4 shrink-0 cursor-pointer"
                  />
                  <span>I accept the privacy policy and account terms. Required.</span>
                </label>
                <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-charcoal/70">
                  <input
                    type="checkbox"
                    data-testid="register-consent-newsletter"
                    checked={form.consent_newsletter}
                    onChange={set("consent_newsletter")}
                    className="brand-check mt-0.5 h-4 w-4 shrink-0 cursor-pointer"
                  />
                  <span>Send me the Rooted Healing newsletter. Optional, and separate from my account.</span>
                </label>
                <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-charcoal/70">
                  <input
                    type="checkbox"
                    data-testid="register-consent-reminders"
                    checked={form.consent_reminders}
                    onChange={set("consent_reminders")}
                    className="brand-check mt-0.5 h-4 w-4 shrink-0 cursor-pointer"
                  />
                  <span>Send me gentle reminders to revisit my wellness discovery. Optional.</span>
                </label>
              </div>

              <button
                type="submit"
                data-testid="register-submit"
                disabled={loading}
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-forest px-8 py-4 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-forest-deep disabled:opacity-60 sm:w-auto"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Create My Free Account
              </button>
            </form>
            <p className="mt-8 text-sm text-charcoal/70">
              Already have an account?{" "}
              <Link to="/login" data-testid="register-login-link" className="font-semibold text-forest underline-offset-4 hover:underline">
                Member Login
              </Link>
            </p>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
